import React, { createContext, useContext, useRef, useCallback, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationState {
  currentPageTitle: string;
  previousPageTitle: string | null;
  previousPath: string | null;
  hasPreviousPage: boolean;
}

interface InternalState extends NavigationState {
  isOptimistic: boolean;
  savedPreviousTitle: string | null;
  savedPreviousPath: string | null;
  savedHasPrevious: boolean;
}

interface NavigationContextValue {
  state: NavigationState;
  setPageTitle: (title: string) => void;
}

interface BackNavigationResult {
  goBack: () => void;
  previousPageTitle: string | null;
}

const NavigationContext = createContext<NavigationContextValue>({} as NavigationContextValue);

function deriveParentPath(path: string): string {
  const segments = path.split('/').filter(Boolean);
  if (segments.length <= 1) return '/home';
  segments.pop();
  return '/' + segments.join('/');
}

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const [, forceUpdate] = React.useState(0);

  const stateRef = useRef<InternalState>({
    currentPageTitle: '',
    previousPageTitle: null,
    previousPath: null,
    hasPreviousPage: false,
    isOptimistic: false,
    savedPreviousTitle: null,
    savedPreviousPath: null,
    savedHasPrevious: false,
  });

  // Detect path change synchronously during render.
  // Optimistically update previousPageTitle so BackArrow shows correct text immediately.
  // This will be confirmed by setPageTitle (real navigation) or reverted (tab switch).
  if (prevPathnameRef.current !== location.pathname) {
    const prev = stateRef.current;
    if (prev.currentPageTitle) {
      stateRef.current = {
        ...prev,
        // Optimistic values — may be reverted if this is a tab switch
        previousPageTitle: prev.isOptimistic ? prev.previousPageTitle : prev.currentPageTitle,
        previousPath: prev.isOptimistic ? prev.previousPath : prevPathnameRef.current,
        hasPreviousPage: true,
        isOptimistic: true,
        // Save last committed values for revert
        savedPreviousTitle: prev.isOptimistic ? prev.savedPreviousTitle : prev.previousPageTitle,
        savedPreviousPath: prev.isOptimistic ? prev.savedPreviousPath : prev.previousPath,
        savedHasPrevious: prev.isOptimistic ? prev.savedHasPrevious : prev.hasPreviousPage,
      };
    } else {
      stateRef.current = { ...prev, isOptimistic: true };
    }
    prevPathnameRef.current = location.pathname;
  }

  const setPageTitle = useCallback((title: string) => {
    const prev = stateRef.current;

    if (prev.isOptimistic) {
      if (title !== prev.currentPageTitle) {
        // Path + title changed → real navigation → commit optimistic values
        stateRef.current = {
          currentPageTitle: title,
          previousPageTitle: prev.previousPageTitle,
          previousPath: prev.previousPath,
          hasPreviousPage: prev.hasPreviousPage,
          isOptimistic: false,
          savedPreviousTitle: null,
          savedPreviousPath: null,
          savedHasPrevious: false,
        };
      } else {
        // Path changed but title same → tab switch → revert
        stateRef.current = {
          ...prev,
          previousPageTitle: prev.savedPreviousTitle,
          previousPath: prev.savedPreviousPath,
          hasPreviousPage: prev.savedHasPrevious,
          isOptimistic: false,
          savedPreviousTitle: null,
          savedPreviousPath: null,
          savedHasPrevious: false,
        };
        forceUpdate(c => c + 1);
      }
    } else {
      // No pending path change → just update title (data load / title refinement)
      stateRef.current = { ...prev, currentPageTitle: title };
    }

    document.title = title ? `${title} - OCP Staff` : 'OCP Staff';
  }, []);

  // Safety net: revert optimistic state if child page didn't call setPageTitle.
  // This handles tab switches where usePageTitle's deps didn't change,
  // so its useLayoutEffect didn't re-fire.
  // React fires child useLayoutEffects before parent useLayoutEffects,
  // so by the time this runs, the page had its chance to call setPageTitle.
  useLayoutEffect(() => {
    if (stateRef.current.isOptimistic) {
      stateRef.current = {
        ...stateRef.current,
        previousPageTitle: stateRef.current.savedPreviousTitle,
        previousPath: stateRef.current.savedPreviousPath,
        hasPreviousPage: stateRef.current.savedHasPrevious,
        isOptimistic: false,
        savedPreviousTitle: null,
        savedPreviousPath: null,
        savedHasPrevious: false,
      };
      forceUpdate(c => c + 1);
    }
  }, [location.pathname]);

  const contextValue: NavigationContextValue = React.useMemo(() => ({
    state: {
      currentPageTitle: stateRef.current.currentPageTitle,
      previousPageTitle: stateRef.current.previousPageTitle,
      previousPath: stateRef.current.previousPath,
      hasPreviousPage: stateRef.current.hasPreviousPage,
    },
    setPageTitle,
  }), [setPageTitle, stateRef.current]);

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export function usePageTitle(title: string): void {
  const { setPageTitle } = useContext(NavigationContext);
  // useLayoutEffect fires BEFORE paint, so reverts happen invisibly
  useLayoutEffect(() => {
    if (title) {
      setPageTitle(title);
    }
  }, [title, setPageTitle]);
}

export function useBackNavigation(fallbackPath?: string): BackNavigationResult {
  const { state } = useContext(NavigationContext);
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = useCallback(() => {
    if (state.hasPreviousPage && state.previousPath) {
      navigate(state.previousPath);
    } else {
      navigate(fallbackPath || deriveParentPath(location.pathname));
    }
  }, [state.hasPreviousPage, state.previousPath, navigate, fallbackPath, location.pathname]);

  return {
    goBack,
    previousPageTitle: state.previousPageTitle,
  };
}
